import { FC, useState } from 'react';
import { VisibilityOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { FileModel } from 'api';
import EmailTemplateConfigurationModal, {
  EmailTemplateSection,
} from 'dashboard409a/components/EmailTemplateConfigurationModal';
import MarkDocumentNaDialog from '../MarkDocumentNaDialog';
import OnboardingEmailDocumentPreviewDialog from '../OnboardingEmailDocumentPreviewDialog';
import { getOnboardingFulfillmentSuffix } from '../OnboardingEmailModal.fulfillment';
import { COMPANY_NAME_PLACEHOLDER } from '../OnboardingEmailModal.templates';
import type { DocumentChecklistItem } from '../OnboardingEmailModal.types';
import { useOnboardingEmailModalContext } from '../OnboardingEmailModalContext';

const OnboardingEmailInputsPanel: FC = () => {
  const {
    templates,
    presets,
    selectedTemplateId,
    setSelectedTemplateId,
    emailSubject,
    introduction,
    setEmailSubject,
    setIntroduction,
    savePresetOpen,
    presetName,
    setPresetName,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleSavePreset,
    handleApplyPreset,
    handleDeletePreset,
    handleResetToDefault,
    checklist,
    toggleDocument,
    markDocumentAsNa,
    isMarkingDocumentNa,
    isLoadingDocuments,
  } = useOnboardingEmailModalContext();

  const [previewFile, setPreviewFile] = useState<FileModel | null>(null);
  const [emailTemplateConfigOpen, setEmailTemplateConfigOpen] = useState(false);
  const [documentToMarkNa, setDocumentToMarkNa] = useState<DocumentChecklistItem | null>(null);

  return (
    <Stack spacing={2}>
      <EmailTemplateSection
        idPrefix="onboarding-email"
        templateSelectLabel="Onboarding request template"
        templates={templates}
        selectedTemplateId={selectedTemplateId}
        setSelectedTemplateId={setSelectedTemplateId}
        emailSubject={emailSubject}
        onConfigure={() => setEmailTemplateConfigOpen(true)}
      />

      <EmailTemplateConfigurationModal
        open={emailTemplateConfigOpen}
        onClose={() => setEmailTemplateConfigOpen(false)}
        idPrefix="onboarding-email"
        title="Configure onboarding email template"
        presets={presets}
        emailSubject={emailSubject}
        setEmailSubject={setEmailSubject}
        introduction={introduction}
        setIntroduction={setIntroduction}
        introductionPlaceholders={[
          { label: COMPANY_NAME_PLACEHOLDER, token: COMPANY_NAME_PLACEHOLDER },
        ]}
        subjectHelperText="Use {{COMPANY_NAME}} for the company."
        introductionHelperText="Use {{COMPANY_NAME}} for the company. The document list and portal link are added automatically."
        savePresetOpen={savePresetOpen}
        presetName={presetName}
        setPresetName={setPresetName}
        handleOpenSavePreset={handleOpenSavePreset}
        handleCloseSavePreset={handleCloseSavePreset}
        handleSavePreset={handleSavePreset}
        handleApplyPreset={handleApplyPreset}
        handleDeletePreset={handleDeletePreset}
        handleResetToDefault={handleResetToDefault}
      />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Documents and questions to request
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Completed items are grayed out and disabled. Uploaded documents can be previewed with the
          eyeball icon; N/A documents can be marked with the N/A button. Answers appear under each
          question.
        </Typography>
        {isLoadingDocuments ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption">Checking uploaded documents and answers…</Typography>
          </Box>
        ) : (
          <Stack spacing={0.5}>
            {checklist.map(item => {
              const uploadedFile = item.uploadedFiles?.[0] ?? null;
              const isQuestion = item.kind === 'question';
              const hasAnswer = Boolean(item.answerDisplay?.trim());
              const childQuestions = item.childQuestions ?? [];
              const isComplete = item.alreadyHave;
              const completeSuffix = isQuestion
                ? ' (answered)'
                : getOnboardingFulfillmentSuffix(
                  item.fulfillmentStatus,
                  Boolean(uploadedFile)
                );
              const slug = item.label.replace(/\s+/g, '-').toLowerCase();
              const isMarkedNa =
                item.fulfillmentStatus === 'not_applicable'
                || item.fulfillmentStatus === 'not_available';
              // Completed documents are previewable: swap the checkbox for the
              // eyeball icon. Completed questions keep a disabled checkbox.
              const showPreviewInsteadOfCheckbox =
                isComplete && !isQuestion && Boolean(uploadedFile) && !isMarkedNa;
              const showMarkNaButton = !isQuestion && !isComplete;
              return (
                <Box key={`${item.kind}-${item.name}`}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    {showPreviewInsteadOfCheckbox ? (
                      <Tooltip title="Preview uploaded document">
                        <IconButton
                          size="small"
                          onClick={() => setPreviewFile(uploadedFile)}
                          aria-label={`Preview ${item.label}`}
                          id={`onboarding-doc-preview-${slug}`}
                          data-testid="onboarding-doc-preview-button"
                          sx={{ p: 0.75 }}>
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Checkbox
                        checked={item.checked}
                        disabled={isComplete}
                        onChange={() => toggleDocument(item.name)}
                        id={`onboarding-doc-${slug}`}
                        size="small"
                        sx={{ pt: 0.25 }}
                      />
                    )}
                    <Box sx={{ flex: 1, pt: 0.5 }}>
                      <Typography
                        variant="body2"
                        color={isComplete ? 'text.disabled' : undefined}>
                        {item.label}
                        {isComplete ? completeSuffix : ''}
                      </Typography>
                      {isQuestion && hasAnswer && (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ display: 'block', mt: 0.25 }}
                          data-testid="onboarding-question-answer">
                          Answer: {item.answerDisplay}
                        </Typography>
                      )}
                      {isQuestion &&
                        childQuestions.map(child => (
                          <Typography
                            key={child.label}
                            variant="caption"
                            color={child.answered ? 'text.disabled' : 'text.secondary'}
                            sx={{ display: 'block', mt: 0.25, pl: 1 }}
                            data-testid="onboarding-question-child-answer">
                            {child.label}
                            {child.answerDisplay ? `: ${child.answerDisplay}` : ''}
                          </Typography>
                        ))}
                      {!isQuestion && isMarkedNa && item.fulfillmentNote ? (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ display: 'block', mt: 0.25 }}
                          data-testid="onboarding-document-na-note">
                          Note: {item.fulfillmentNote}
                        </Typography>
                      ) : null}
                    </Box>
                    {showMarkNaButton ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setDocumentToMarkNa(item)}
                        disabled={isMarkingDocumentNa}
                        id={`onboarding-doc-mark-na-${slug}`}
                        data-testid="onboarding-doc-mark-na-button"
                        sx={{ minWidth: 0, px: 1, mt: 0.25 }}
                      >
                        N/A
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
      <OnboardingEmailDocumentPreviewDialog
        open={Boolean(previewFile)}
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
      <MarkDocumentNaDialog
        item={documentToMarkNa}
        isSubmitting={isMarkingDocumentNa}
        onClose={() => setDocumentToMarkNa(null)}
        onConfirm={async (item, status, note) => {
          await markDocumentAsNa(item, status, note);
          setDocumentToMarkNa(null);
        }}
      />
    </Stack>
  );
};

export default OnboardingEmailInputsPanel;
